import React, { useState } from 'react';
import { Col, Row } from 'antd';
import { Title, Icon, Body, Button } from '@cognite/cogs.js';
import { HorizontalDivider, SeverityTag, VerticalDivider } from 'components';
import styled from 'styled-components/macro';
import {
  convertCronExpressionToTimeAndDays,
  getConditionsCount,
  getDaysOptions,
} from 'utils/project-ruleset';
import { Rule, RuleSeverity } from 'types/dt-graphql-apollo-v2';

// TODO Fix this import :(
import { LogicalRule, parseFilterToLogicalRule } from 'utils/rules';
import { ConditionsTreeSummary } from '../../../components/Schemas/ViewSchemaPage/ProjectRuleset/components/Conditions/ConditionsTreeSummary';

export const RuleDetails = ({ selectedRule }: { selectedRule?: Rule }) => {
  const [showRulesetFilter, setShowRulesetFilter] = useState(false);
  const [showRuleFilter, setShowRuleFilter] = useState(false);
  const selectedRuleset = selectedRule?.rulesets
    ? selectedRule?.rulesets.items[0]
    : null;

  // Rule filters
  const ruleFiltersTree: LogicalRule = parseFilterToLogicalRule(
    selectedRule?.filters || '{}'
  );
  const { valid: validRuleFilters } = getConditionsCount(ruleFiltersTree);

  // Rule conditions
  const ruleConditionsTree: LogicalRule | null = parseFilterToLogicalRule(
    selectedRule?.rules || '{}'
  );

  // Ruleset filters
  const rulesetFiltersTree: LogicalRule | null = selectedRuleset
    ? parseFilterToLogicalRule(selectedRuleset.filters || '{}')
    : null;
  const { valid: validRulesetFilters } =
    selectedRuleset && !!rulesetFiltersTree
      ? getConditionsCount(rulesetFiltersTree)
      : { valid: 0 };

  // Milestone schedule
  const convertSchedule = selectedRule?.milestones
    ? convertCronExpressionToTimeAndDays(
        selectedRule?.milestones.items[0]?.schedule
      )
    : null;

  const scheduleDays =
    (convertSchedule?.days && getDaysOptions(convertSchedule?.days)) || [];
  const scheduleDayLabels = scheduleDays.map((option) => option.label);

  const runDuringActiveTimeStart = selectedRule?.milestones
    ? selectedRule?.milestones.items[0]?.startActiveTime ===
      selectedRule?.milestones.items[0]?.startScheduleTime
    : false;

  const runDuringActiveTimeEnd = selectedRule?.milestones
    ? selectedRule?.milestones.items[0]?.endActiveTime ===
      selectedRule?.milestones.items[0]?.endScheduleTime
    : false;

  const renderGeneralInformation = () => (
    <StyledContainer>
      <StyledBorderedContainer>
        <StyledColumn>
          <StyledOverline>Description</StyledOverline>
          <Body level={6}>{selectedRule?.description || '-'}</Body>
        </StyledColumn>

        <StyledColumn>
          <StyledOverline>Error Message</StyledOverline>
          <Body level={6}>{selectedRule?.errorMessage || '-'}</Body>
        </StyledColumn>

        {selectedRule?.severity && (
          <StyledColumn>
            <StyledOverline>Severity</StyledOverline>
            <SeverityTag severity={selectedRule?.severity as RuleSeverity} />
          </StyledColumn>
        )}
      </StyledBorderedContainer>
      <StyledColumn>
        <StyledOverline>Runs</StyledOverline>
        {convertSchedule ? (
          <StyledRow align="middle">
            <StyledScheduleDays align="middle">
              {scheduleDayLabels.map((day) => (
                <Body level={6}>{day},</Body>
              ))}
            </StyledScheduleDays>
            <Body level={6}>{convertSchedule.time}</Body>

            {runDuringActiveTimeStart && runDuringActiveTimeEnd && (
              <>
                <VerticalDivider />
                <Body level={6}>Run during active milestone</Body>
              </>
            )}
          </StyledRow>
        ) : (
          <Body level={6}>No schedule found</Body>
        )}
      </StyledColumn>
    </StyledContainer>
  );

  const renderRulesetFilters = () => (
    <StyledRuleContainer>
      <ConditionsTreeSummary
        prefix={`ruledetails-rulesetfilter-${selectedRuleset?.externalId}`}
        conditionsTree={rulesetFiltersTree}
      />
    </StyledRuleContainer>
  );

  const renderRuleFilters = () => (
    <StyledRuleContainer>
      <ConditionsTreeSummary
        prefix={`ruledetails-rulefilter-${selectedRule?.externalId}`}
        conditionsTree={ruleFiltersTree}
      />
    </StyledRuleContainer>
  );

  const renderRuleConditions = () => (
    <ConditionsTreeSummary
      prefix={`ruledetails-rulecondition-${selectedRule?.externalId}`}
      conditionsTree={ruleConditionsTree}
      maxTreeHeight={500}
    />
  );

  const renderFilters = () => (
    <StyledColumn>
      <StyledOverline>Filter</StyledOverline>
      <StyledRow>
        <Title level={6}>Schema type:</Title>
        <Title level={6}>
          {selectedRuleset
            ? selectedRuleset.schemaType
            : selectedRule?.schemaType}
        </Title>
      </StyledRow>

      {selectedRuleset && (
        <>
          <StyledRulesetRow align="middle">
            <Icon type="Link" />
            <Body level={6}>{selectedRuleset.name}</Body>
            <VerticalDivider />
            <StyledRulesetRowFilter>
              <Button
                type="ghost"
                icon={showRulesetFilter ? 'ChevronUp' : 'ChevronDown'}
                iconPlacement="right"
                onClick={() => setShowRulesetFilter(!showRulesetFilter)}
                disabled={validRulesetFilters === 0}
              >
                <span>
                  {`${validRulesetFilters || 0} ${
                    validRulesetFilters === 1 ? 'condition' : 'conditions'
                  }`}
                </span>
              </Button>
            </StyledRulesetRowFilter>
          </StyledRulesetRow>
          {showRulesetFilter && renderRulesetFilters()}
          <StyledRulesetRowFilter>
            <Button
              type="secondary"
              icon={showRuleFilter ? 'ChevronUp' : 'ChevronDown'}
              iconPlacement="right"
              onClick={() => setShowRuleFilter(!showRuleFilter)}
              disabled={validRuleFilters === 0}
            >
              <span>
                {`${validRuleFilters || 0} rule-specific ${
                  validRuleFilters === 1 ? 'condition' : 'conditions'
                }`}
              </span>
            </Button>
          </StyledRulesetRowFilter>
          {showRuleFilter && renderRuleFilters()}
        </>
      )}
    </StyledColumn>
  );

  const renderConditions = () => (
    <StyledColumn>
      <StyledOverline>Rule</StyledOverline>
      {renderRuleConditions()}
    </StyledColumn>
  );

  return (
    <>
      {renderGeneralInformation()}
      <HorizontalDivider />
      {renderFilters()}
      <HorizontalDivider />
      {renderConditions()}
    </>
  );
};

const StyledColumn = styled(Col)`
  line-height: 20px;
  margin: 1rem;
`;

const StyledOverline = styled.div`
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  font-family: 'Inter', sans-serif;
  color: var(--cogs-greyscale-grey7);
`;

const StyledRow = styled(Row)`
  gap: 0.5rem;
  .cogs-title-6 {
    font-weight: 500;
    color: var(--cogs-greyscale0grey8);
  }
`;

const StyledScheduleDays = styled(Row)`
  gap: 0.3rem;
`;

const StyledRulesetRow = styled(Row)`
  margin: 0.5rem 0;
  gap: 1rem;
  display: grid;
  grid-template-columns: 10px fit-content(100%) 1px auto;
`;

const StyledRulesetRowFilter = styled.div`
  > Button {
    margin-left: -1rem;
  }
`;

const StyledContainer = styled.div`
  padding: 1rem 0;
`;

const StyledRuleContainer = styled.div`
  padding: 0.5rem 1rem;
`;

const StyledBorderedContainer = styled.div`
  border-bottom: solid 1px var(--cogs-border-default);
`;
